if (!customElements.get('product-accordion')) {
  customElements.define(
    'product-accordion',
    class ProductAccordion extends HTMLElement {
      constructor() {
        super();
        this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
        this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
      }

      connectedCallback() {
        this.addEventListener('click', this.handleClick.bind(this));
        this.addEventListener('keydown', this.handleKeydown.bind(this));
        this.addEventListener('shopify:block:select', this.handleBlockSelect.bind(this));

        this.boundHandleResize = this.debounce(this.handleResize.bind(this), 200);
        window.addEventListener('resize', this.boundHandleResize);

        this.checkActivePanelClamp();
      }

      disconnectedCallback() {
        window.removeEventListener('resize', this.boundHandleResize);
      }

      debounce(fn, wait) {
        let timeout;
        return (...args) => {
          clearTimeout(timeout);
          timeout = setTimeout(() => fn(...args), wait);
        };
      }

      handleResize() {
        this.panels.forEach((panel) => delete panel.dataset.clampChecked);
        this.checkActivePanelClamp();
      }

      checkActivePanelClamp() {
        const activePanel = this.panels.find((panel) => !panel.hidden);
        if (activePanel) this.checkClamp(activePanel);
      }

      checkClamp(panel) {
        const content = panel.querySelector('.product-accordion__panel-content');
        const readMoreButton = panel.querySelector('.product-accordion__read-more');
        if (!content || !readMoreButton || panel.dataset.clampChecked) return;

        const isClamped = content.scrollHeight > content.clientHeight + 1;
        readMoreButton.hidden = !isClamped;
        panel.dataset.clampChecked = 'true';
      }

      toggleReadMore(button) {
        const content = button.previousElementSibling;
        if (!content) return;

        const expanded = button.getAttribute('aria-expanded') === 'true';
        content.classList.toggle('is-expanded', !expanded);
        button.setAttribute('aria-expanded', String(!expanded));
        button.textContent = expanded ? button.dataset.readMoreLabel : button.dataset.readLessLabel;
      }

      handleClick(event) {
        const tab = event.target.closest('[role="tab"]');
        if (tab) {
          this.activateTab(tab);
          return;
        }

        const readMoreButton = event.target.closest('.product-accordion__read-more');
        if (readMoreButton) this.toggleReadMore(readMoreButton);
      }

      handleKeydown(event) {
        const currentTab = event.target.closest('[role="tab"]');
        if (!currentTab) return;

        const currentIndex = this.tabs.indexOf(currentTab);
        let newIndex = null;

        switch (event.key) {
          case 'ArrowRight':
          case 'ArrowDown':
            newIndex = (currentIndex + 1) % this.tabs.length;
            break;
          case 'ArrowLeft':
          case 'ArrowUp':
            newIndex = (currentIndex - 1 + this.tabs.length) % this.tabs.length;
            break;
          case 'Home':
            newIndex = 0;
            break;
          case 'End':
            newIndex = this.tabs.length - 1;
            break;
          default:
            return;
        }

        event.preventDefault();
        const newTab = this.tabs[newIndex];
        this.activateTab(newTab);
        newTab.focus();
      }

      handleBlockSelect(event) {
        const blockId = event.detail.blockId;
        const tab = this.tabs.find((tab) => tab.getAttribute('aria-controls') === `ProductAccordionPanel-${blockId}`);
        if (tab) this.activateTab(tab);
      }

      activateTab(tab) {
        if (tab.classList.contains('is-active')) return;

        this.tabs.forEach((otherTab) => {
          otherTab.classList.remove('is-active');
          otherTab.setAttribute('aria-selected', 'false');
          otherTab.setAttribute('tabindex', '-1');
        });

        this.panels.forEach((panel) => {
          panel.classList.remove('is-active');
          panel.hidden = true;
        });

        tab.classList.add('is-active');
        tab.setAttribute('aria-selected', 'true');
        tab.setAttribute('tabindex', '0');

        const panel = this.querySelector(`#${tab.getAttribute('aria-controls')}`);
        if (panel) {
          panel.classList.add('is-active');
          panel.hidden = false;
          this.checkClamp(panel);
        }
      }
    }
  );
}
